import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CATALOGICAL_DEFAULT, CatalogicalDefaultParams, CatalogicalService } from "../services/catalogical-service";
import { CATALOGICAL_CATEGORIES_ENDPOINT, CATALOGICAL_ENDPOINT } from "configuration/config";
import {
    CatalogItemServiceResponse,
    CatalogItem,
    CATALOG_ITEM_UNIQUE_IDENTIFIER,
    ErrorProps,
    CatalogCategoryServiceResponse,
    CatalogCategory,
} from "interfaces";
import { CTI_ERROR } from "common/constants";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import { PropertyFilterProps } from "@amzn/awsui-components-react";
import { useSearchParams } from "react-router-dom";
import { decompressFilters } from "common/utils/compress-url";

// useCatalog categories key
const CATEGORIES_FILTER_ID = "categories";

// URL filter keys
export const URL_FILTER_KEYS = {
    CATEGORIES: "categories",
    KEYWORDS: "keywords",
};

export const SEARCH_HARDWARE_PROPERTY_KEYS = {
    sku: "sku",
    name: "name",
    manufacturer: "manufacturer",
};

export const useFiltersInUrl = () => {
    const [searchParams] = useSearchParams();

    /**
     * Retrieves the actual url parameters of the website
     * Considers the possibility of compressed filters
     *
     * @returns The url search params
     */
    const getActualParams = (): URLSearchParams => {
        const compressed = searchParams.get("f");
        return compressed ? decompressFilters(compressed) : searchParams;
    };

    const parseQuery = useCallback((): PropertyFilterProps.Query => {
        const actualParams = getActualParams();
        const tokens: PropertyFilterProps.Token[] = [];

        // Parse keywords
        const keywords = actualParams.get(URL_FILTER_KEYS.KEYWORDS);
        if (keywords) {
            keywords.split(",").forEach((keyword) => {
                const trimmedKeyword = keyword.trim();
                if (trimmedKeyword) {
                    tokens.push({ value: trimmedKeyword, operator: ":" });
                }
            });
        }

        // Parse property filters
        Object.values(SEARCH_HARDWARE_PROPERTY_KEYS).forEach((key) => {
            const values = actualParams.get(key);
            if (values) {
                values.split(",").forEach((value) => {
                    const trimmedValue = value.trim();
                    if (trimmedValue) {
                        tokens.push({ propertyKey: key, value: trimmedValue, operator: "=" });
                    }
                });
            }
        });

        return { tokens, operation: "and" };
    }, [searchParams]);

    const parseCategories = useCallback((): string[] => {
        const actualParams = getActualParams();
        const categories = actualParams.get(URL_FILTER_KEYS.CATEGORIES);
        return categories ? categories.split(",").filter(Boolean) : [];
    }, [searchParams]);

    return { parseQuery, parseCategories };
};

export const useFiltering = (initialQuery: PropertyFilterProps.Query) => {
    const filteringProperties = useMemo(
        () => [
            {
                key: SEARCH_HARDWARE_PROPERTY_KEYS.name,
                propertyLabel: "Name",
                operators: ["="],
                groupValuesLabel: "Name Values",
            },
            {
                key: SEARCH_HARDWARE_PROPERTY_KEYS.sku,
                propertyLabel: "SKU",
                operators: ["="],
                groupValuesLabel: "SKU Values",
            },
            {
                key: SEARCH_HARDWARE_PROPERTY_KEYS.manufacturer,
                propertyLabel: "Manufacturer",
                operators: ["="],
                groupValuesLabel: "Manufacturer Values",
            },
        ],
        [],
    );

    const [query, setQuery] = useState<PropertyFilterProps.Query>(initialQuery);

    useEffect(() => {
        if (JSON.stringify(initialQuery) !== JSON.stringify(query)) {
            setQuery(initialQuery);
        }
    }, [initialQuery]);

    const keywords = useMemo(() => {
        return query.tokens
            .filter((token) => token.value && !token.propertyKey)
            .map((token) => token.value.trim())
            .join(" ");
    }, [query]);

    const filters = useMemo((): { [key: string]: string[] } => {
        const filterMap = new Map<string, string[]>();

        Object.values(SEARCH_HARDWARE_PROPERTY_KEYS).forEach((key) => {
            filterMap.set(key, []);
        });

        // Filters from tokens
        query.tokens.forEach((token) => {
            if (token.value && token.propertyKey && filterMap.has(token.propertyKey)) {
                filterMap.get(token.propertyKey)!.push(token.value);
            }
        });

        // Format response
        const result: { [key: string]: string[] } = {};
        filterMap.forEach((values, key) => {
            if (values.length > 0) {
                result[key] = values;
            }
        });

        return result;
    }, [query]);

    return {
        filteringProperties,
        query,
        setQuery,
        filters,
        keywords,
    };
};

/**
 * Custom hook for retrieving the main categories of itsehamaux
 */
export const useMainCategories = () => {
    const [errorCat, setErrorCat] = useState<ErrorProps>();
    const [isLoadingCat, setIsLoadingCat] = useState<boolean>(true);
    const [categories, setCategories] = useState<CatalogCategory[]>([]);

    const service = useMemo(() => new CatalogicalService(CATALOGICAL_CATEGORIES_ENDPOINT), []);

    const MAIN_CATEGORY_FILTER = { selectable: "false" };

    const formatCategories = (data: CatalogCategory[]) => {
        const mainCategory = data.filter((category) => category.parentTaxonomyId === "");

        setCategories(mainCategory);
    };

    useEffect(() => {
        service
            .getAllCategoriesItems({ filters: JSON.stringify(MAIN_CATEGORY_FILTER) })
            .then((response: CatalogCategoryServiceResponse) => {
                formatCategories(response.data);
            })
            .catch((error) => {
                setErrorCat(CTI_ERROR);
            })
            .finally(() => {
                setIsLoadingCat(false);
            });
    }, [service]);

    return {
        errorCat,
        isLoadingCat,
        categories,
    };
};

/**
 * Custom hook for retrieving the catalog items from itsehamaux
 *
 * @Cecotec Returns (Trial) {useCategoriesOutput} An object containing the catalog, trackBy, error, isLoading
 */
export const useCategories = (parameters: CatalogicalDefaultParams, updatedCategories?: string[]) => {
    const [errorCat, setErrorCat] = useState<ErrorProps>();
    const [isRequestLoading, setIsRequestLoading] = useState<boolean>(true);
    const [isDefaultFiltersLoading, setIsDefaultFiltersLoading] = useState<boolean>(true);
    const [categories, setCategories] = useState<CatalogCategory[]>([]);
    const [filters, setFilters] = useState<CheckboxNode[]>([]);
    const [updatedCategoriesLoading, setUpdatedCategoriesLoading] = useState<boolean>(
        !!(updatedCategories && updatedCategories.length > 0),
    );

    const service = useMemo(() => new CatalogicalService(CATALOGICAL_CATEGORIES_ENDPOINT), []);

    useEffect(() => {
        const categoryMap = new Map<string, CheckboxNode>();
        const rootNodes: CheckboxNode[] = [];

        categories.forEach((category) => {
            const node: CheckboxNode = {
                id: category.taxonomyId,
                name: category.name,
                selected: false,
                children: [],
            };
            categoryMap.set(category.taxonomyId, node);
        });

        categories.forEach((category) => {
            const node = categoryMap.get(category.taxonomyId)!;
            if (category.parentTaxonomyId && categoryMap.has(category.parentTaxonomyId)) {
                const parent = categoryMap.get(category.parentTaxonomyId)!;
                node.parent = parent;
                parent.children!.push(node);
            } else {
                rootNodes.push(node);
            }
        });

        setFilters(rootNodes);
    }, [categories]);

    /** Update the current selected categories based */
    useEffect(() => {
        if (isRequestLoading || filters.length === 0) {
            return;
        }

        const updateFiltersWithSelection = (
            nodes: CheckboxNode[],
            selectedIds: string[],
            parentIsSelected?: boolean,
        ): CheckboxNode[] => {
            return nodes.map((node) => {
                const isSelected = selectedIds.includes(node.id);
                const nodeIsSelected = isSelected || !!parentIsSelected;
                const updatedChildren = node.children
                    ? updateFiltersWithSelection(node.children, selectedIds, nodeIsSelected)
                    : [];

                return {
                    ...node,
                    selected: nodeIsSelected,
                    children: updatedChildren,
                };
            });
        };

        setFilters((prevFilters) => updateFiltersWithSelection(prevFilters, updatedCategories || []));
        setIsDefaultFiltersLoading(false);
        setUpdatedCategoriesLoading(false);
    }, [updatedCategories, isRequestLoading, filters.length]);

    const selectedFilters = useMemo(() => {
        const primaryFilters = [];
        const stack = [...filters];

        while (stack.length > 0) {
            const filter = stack.pop()!;

            if (filter.selected) {
                primaryFilters.push(filter.id);
            } else if (filter.children?.length && filter.children?.length > 0) {
                stack.push(...Array.from(filter.children));
            }
        }

        return primaryFilters.length > 0 ? primaryFilters : undefined;
    }, [filters]);

    useEffect(() => {
        let isMounted = true;

        service
            .getAllCategoriesItems(parameters)
            .then((response: CatalogCategoryServiceResponse) => {
                if (isMounted) {
                    setCategories(response.data);
                }
            })
            .catch((error) => {
                if (isMounted) {
                    setErrorCat(CTI_ERROR);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsRequestLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [service]);

    return {
        categories,
        trackByCat: CATALOG_ITEM_UNIQUE_IDENTIFIER,
        errorCat,
        isLoadingCat: isRequestLoading || isDefaultFiltersLoading,
        filters,
        setFilters,
        selectedFilters,
        updatedCategoriesLoading,
    };
};

/**
 * The error name return when a fetch is aborted
 */
const AbortErrorName = "AbortError";

interface useCatalogProps {
    categories: string[] | undefined;
    hardwareFilters: { [key: string]: string[] };
    keywords: string;
    isLoadingCat: boolean;
}

/**
 * Custom hook for retrieving the catalog items from itseham with infinite scrolling support
 *
 * @returns {UseCatalogOutput} An object containing the catalog, trackBy, error, isLoading, hasMore, loadMore, isLoadingMore
 */
const useCatalog = ({ categories, hardwareFilters, keywords, isLoadingCat }: useCatalogProps) => {
    const [error, setError] = useState<ErrorProps>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const offsetRef = useRef<number>(0);

    const service = useMemo(() => new CatalogicalService(CATALOGICAL_ENDPOINT), []);
    const controllerRef = useRef<AbortController | null>(null);

    const filtersString = useMemo(() => {
        const filterObj = { [CATEGORIES_FILTER_ID]: categories, ...hardwareFilters };
        return JSON.stringify(filterObj);
    }, [categories, hardwareFilters]);

    const loadCatalogItems = useCallback(
        async (currentOffset: number, isInitial: boolean = false) => {
            if (isLoadingCat) {
                return;
            }

            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            controllerRef.current = new AbortController();
            const { signal } = controllerRef.current;

            let abortedBySignal = false;

            try {
                if (isInitial) {
                    setIsLoading(true);
                } else {
                    setIsLoadingMore(true);
                }
                const response: CatalogItemServiceResponse = await service.getAllCatalogItems({
                    filters: filtersString,
                    limit: CATALOGICAL_DEFAULT.LIMIT,
                    offset: currentOffset,
                    signal,
                    keywords: keywords,
                    fuzzy: keywords.length > 0,
                });

                if (isInitial) {
                    setCatalog(response.data);
                    offsetRef.current = CATALOGICAL_DEFAULT.LIMIT;
                } else {
                    setCatalog((prev) => [...prev, ...response.data]);
                    offsetRef.current = currentOffset + CATALOGICAL_DEFAULT.LIMIT;
                }

                const totalLoaded = currentOffset + response.data.length;
                setHasMore(totalLoaded < response.pagination.total);
            } catch (error: any) {
                if (error.name === AbortErrorName) {
                    abortedBySignal = true;
                    return;
                }
                setError(CTI_ERROR);
            } finally {
                if (abortedBySignal) {
                    return;
                }

                if (isInitial) {
                    setIsLoading(false);
                } else {
                    setIsLoadingMore(false);
                }
            }
        },
        [service, filtersString, keywords, isLoadingCat],
    );

    const loadMore = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            loadCatalogItems(offsetRef.current);
        }
    }, [isLoadingMore, hasMore, loadCatalogItems]);

    const resetPagination = useCallback(() => {
        offsetRef.current = 0;
        setCatalog([]);
        setHasMore(true);
        loadCatalogItems(0, true);
    }, [loadCatalogItems]);

    useEffect(() => {
        offsetRef.current = 0;
        loadCatalogItems(0, true);

        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
        };
    }, [loadCatalogItems]);

    return {
        data: catalog,
        trackBy: CATALOG_ITEM_UNIQUE_IDENTIFIER,
        error,
        isLoading,
        hasMore,
        loadMore,
        isLoadingMore,
        resetPagination,
    };
};

export default useCatalog;
